import type {
  AmbulanceListQueryParams,
  AmbulanceListResponse,
  AmbulanceRouteQueryParams,
  AmbulanceRouteResponse,
  ReportListQueryParams,
  ReportManualCreateBody,
  ReportManualCreateResponse,
  ReportTodayQueryParams,
  ReportTodayResponse,
} from '@/domains/traffic-monitoring/types/psc-dashboard.types';
import { request } from '@/shared/utils/request';

const buildQuery = (params?: Record<string, unknown>): string => {
  const search = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        search.append(key, String(value));
      }
    });
  }
  return search.toString();
};

export const pscDashboardApi = {
  // GET /api/dsh/report/today -> [{ report_id, event_type, ambulance_reports[], ... }]
  getReportsToday: async (params?: ReportTodayQueryParams): Promise<ReportTodayResponse> => {
    const qs = buildQuery(params as Record<string, unknown> | undefined);
    return request.get<ReportTodayResponse>(qs ? `/report/today?${qs}` : '/report/today');
  },

  // GET /api/dsh/report/list -> full report history (no 24h window), newest
  // first. Same item shape as /report/today; backs the Report Management screen.
  // Optional date_from/date_to (YYYY-MM-DD, inclusive WIB days, either side
  // open-ended) filter by received time.
  getReportsList: async (params?: ReportListQueryParams): Promise<ReportTodayResponse> => {
    const qs = buildQuery(params as Record<string, unknown> | undefined);
    return request.get<ReportTodayResponse>(qs ? `/report/list?${qs}` : '/report/list');
  },

  // GET /api/dsh/ambulance/list -> { date, total_count, page, size, list[] }
  getAmbulanceList: async (params?: AmbulanceListQueryParams): Promise<AmbulanceListResponse> => {
    const qs = buildQuery(params as Record<string, unknown> | undefined);
    return request.get<AmbulanceListResponse>(qs ? `/ambulance/list?${qs}` : '/ambulance/list');
  },

  // GET /api/dsh/ambulance/route/list -> GeoJSON FeatureCollection of active ambulance trails
  getAmbulanceRouteList: async (
    params?: AmbulanceRouteQueryParams
  ): Promise<AmbulanceRouteResponse> => {
    const qs = buildQuery(params as Record<string, unknown> | undefined);
    return request.get<AmbulanceRouteResponse>(
      qs ? `/ambulance/route/list?${qs}` : '/ambulance/route/list'
    );
  },

  // POST /api/dsh/report/manual -> { report_id, report_code_id }
  postReportManualCreate: async (
    body: ReportManualCreateBody
  ): Promise<ReportManualCreateResponse> => {
    return request.post<ReportManualCreateResponse>('/report/manual', body);
  },

  // GET /api/dsh/report/export/id -> .xlsx attachment (Indonesian report format)
  exportReportsIndonesian: async (params: {
    date_from: string;
    date_to: string;
  }): Promise<Blob> => {
    const qs = buildQuery(params);
    return request.get<Blob>(`/report/export/id?${qs}`, { responseType: 'blob' });
  },

  // GET /api/dsh/report/export/en -> .xlsx attachment (English report format)
  exportReportsEnglish: async (params: { date_from: string; date_to: string }): Promise<Blob> => {
    const qs = buildQuery(params);
    return request.get<Blob>(`/report/export/en?${qs}`, { responseType: 'blob' });
  },
};
